import {
  Spacer,
  TextBase,
  TextButton,
  TextCaption,
  TooltipBubble,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import type React from 'react';
import { Screenshot } from '../Screenshot';

export interface TileImageSquareProps {
  onClick?: () => void;
  image?: string;
  alt?: string;
  title: string;
  caption?: string;
  createTime?: string;
  icon?: React.ReactNode;
  imageTitle?: string;
  imageTitleIcon?: React.ReactNode;
  imageOnClick?: () => void;
  background?: boolean;
  smallImage?: boolean;
  wide?: boolean;
  small?: boolean;
  dropdown?: React.ReactNode;
  button?: React.ReactNode;
  selected?: boolean;
  to?: string;
  imageLabel?: React.ReactNode;
  isOverlayDemo?: boolean;
  isOverlayTour?: boolean;
}

export const TileImageSquare = ({
  onClick,
  imageOnClick,
  image,
  title,
  caption,
  createTime,
  alt = 'image alt',
  icon,
  imageTitle,
  imageTitleIcon,
  dropdown,
  button,
  background,
  smallImage,
  selected,
  to,
  imageLabel,
  isOverlayDemo = false,
  isOverlayTour = false,
}: TileImageSquareProps) => {
  const hasAction = dropdown || button;
  const defaultClasses =
    'group flex flex-col rounded-md h-60 p-2.5 hover:shadow-md  w-ds-59 h-ds-48 overflow-hidden';

  return (
    <div
      onClick={onClick}
      className={classNames(defaultClasses, {
        'hover:bg-ds-natural-50': !selected,
        'bg-ds-natural-50': background && !selected,
        'border-ds-natural-100 border bg-ds-natural-100': selected,
        'cursor-pointer': onClick,
      })}
    >
      <a
        href={to}
        rel="noopener noreferrer"
        onClick={(event) => {
          if (imageOnClick) {
            event.preventDefault();
            event.stopPropagation();
            imageOnClick();
          }
        }}
        className={classNames(
          'relative overflow-hidden rounded-md h-ds-34 flex-shrink-0',
          {
            'hover:cursor-pointer': imageOnClick,
            'border border-ds-natural-100': !smallImage,
          }
        )}
      >
        <div className="h-full">
          <Screenshot
            withPadding={smallImage}
            withWhiteBorder={smallImage}
            noBorder={smallImage}
            image={image}
            alt={alt}
            overlay={
              imageTitle && (
                <div className="group-hover:visible truncate invisible absolute inset-0 z-45 bg-ds-natural-700-a rounded-md">
                  <div className="h-full flex justify-center items-center truncate">
                    {imageTitleIcon}
                    {imageTitleIcon && <Spacer amount={0.5} />}
                    <TextButton
                      label={imageTitle}
                      color="text-ds-accent3-100"
                    />
                  </div>
                </div>
              )
            }
            isOverlayDemo={isOverlayDemo}
            isOverlayTour={isOverlayTour}
          />
          {imageLabel && (
            <div className="absolute -mt-6 ml-2">{imageLabel}</div>
          )}
        </div>
      </a>
      <div
        className={classNames('flex items-center justify-between mt-2', {
          'group-hover:hidden': hasAction,
        })}
      >
        {icon}
        <Spacer amount={0.5} />
        <div className="flex flex-col truncate overflow-hidden text-ds-natural-400 w-full pr-2.5">
          <TextBase
            ellipsis
            label={title}
            color={smallImage ? 'text-ds-natural-400' : undefined}
          />
          <div className="flex gap-1">
            {caption && (
              <TextCaption
                label={caption}
                color="text-ds-natural-300"
                ellipsis
              />
            )}
            {caption && createTime && (
              <TextCaption
                label="|"
                color="text-ds-natural-300"
              />
            )}
            {createTime && (
              <TextCaption
                label={createTime}
                color="text-ds-natural-300"
              />
            )}
          </div>
        </div>
      </div>
      {hasAction && (
        <div className="group-hover:visible flex flex-col invisible w-full">
          <TooltipBubble
            placement="top"
            title={title}
          >
            <div className="flex items-center justify-between my-2">
              <TextBase
                label={title}
                color="text-ds-natural-600"
                ellipsis
              />
              <div className="hover:cursor-pointer">{dropdown}</div>
            </div>
          </TooltipBubble>
          {button}
        </div>
      )}
    </div>
  );
};
